import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '@Utils/getApiUrl';

import {
  TypeDict,
  CompanyDict,
  CountryDict,
  ProjectNDepartments,
  DeptProjList,
} from '@Types/index';
import { ProjectDepartmentsRequest } from '@Types/api/common';
import { RequestListSearchResponse } from '@Types/api/requestList';
import { extractAuthToken } from '@Utils/api/extractAuthToken';
import {
  ActivityResources,
  CostCenter,
  Organization,
  PlantResources,
  Project,
  RequisitionResources,
} from '@Types/api/common/dictionary';
import { customBaseQuery } from '@Utils/api/customBaseQuery';
import { VendorDTO } from '@Types/api/form/tenderRequest/VendorDTO';
import { Currency } from '@Types/api/common/dictionary/Currency';
import PlantResource from '@Types/api/common/dictionary/MaterialRequest/PlantResource';
import { LegalInfo } from '@Types/api/common/dictionary/LegalInfo';
import { AssetsResponse } from '@Types/api/common/dictionary/AssetsResponse';

const baseUrl = getApiUrl();

export const dictionaryApi = createApi({
  reducerPath: ApiReducerPath.API_GET_DICTIONARY,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getProfileAssets: builder.query<AssetsResponse, void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/emp/assets`,
        method: 'get',
      }),
    }),
    getProfileLegalInfo: builder.query<LegalInfo, void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/emp/legal-info`,
        method: 'get',
      }),
    }),
    getDictionaryCurrency: builder.query<Currency[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/currencies`,
        method: 'get',
      }),
    }),
    getDictionaryRequestType: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'requestType' }],
      }),
    }),
    getDictionaryLeaveType: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'leaveType' }],
      }),
    }),
    getDictionaryLeaveStatuses: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'LeaveStatus' }],
      }),
      transformResponse(response: TypeDict[]) {
        // remove status "Awaiting Approval of Head of HR" with code 6 from response as duplicate
        const filtredResponse = response.filter((item) => item.codeNum !== 6);

        return filtredResponse;
      },
    }),
    getDictionaryITReqItems: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'ITReqItems' }],
      }),
      transformResponse(response: TypeDict[]) {
        // remove "Change Management" item from response
        // this item not used in current system for it requisition
        const filtredResponse = response.filter((item) => item.codeNum !== 12);

        return filtredResponse;
      },
    }),
    getDictionaryITReqStatus: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'ITReqStatus' }],
      }),
    }),
    getDictionarySIMCapValue: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'SIMCapValue' }],
      }),
    }),
    getDictionaryITAccessories: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'ITAccessories' }],
      }),
      transformResponse(response: TypeDict[]) {
        // remove "Power extension" item from response
        // this item not used in current system for it requisition
        const filtredResponse = response
          .sort((a, b) => a.codeDesc.localeCompare(b.codeDesc))
          .filter((item) => item.codeNum !== 3);

        return filtredResponse;
      },
    }),
    getDictionaryPassportType: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'PPPurpose' }],
      }),
    }),
    getDictionaryPassportStatuses: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'PPStatus' }],
      }),
    }),
    getDictionaryRequisitionStatuses: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'RequisitionStatus' }],
      }),
    }),
    getDictionarySalaryType: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'SCPurpose' }],
      }),
    }),
    getDictionarySalaryStatuses: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'SCStatus' }],
      }),
    }),
    getDictionaryOverseasStatuses: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'OverseasMissionStatus' }],
      }),
    }),
    getDictionaryOverseasSponsors: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'OverseasSponsor' }],
      }),
    }),
    getDictionaryCompanies: builder.query<CompanyDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/companies`,
        method: 'get',
      }),
    }),
    getDictionaryCountries: builder.query<CountryDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/countries`,
        method: 'get',
      }),
      transformResponse(response: CountryDict[]) {
        return response?.sort((a, b) =>
          a.countryName.localeCompare(b.countryName)
        );
      },
    }),
    getDictionaryResumptionStatuses: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'ResStatus' }],
      }),
    }),
    getDictionaryResumptionWorkShift: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'WorkShift' }],
      }),
    }),
    getDictionaryAttendanceStatuses: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'TAReqStatus' }],
      }),
      transformResponse(response: TypeDict[]) {
        // changed by ticket 1698
        const changedResponseStatus = response.map((item) => {
          if (item.codeDesc === 'Cancel') {
            return {
              ...item,
              codeDesc: 'Cancelled',
            };
          }
          return item;
        });
        return changedResponseStatus;
      },
    }),
    getDictionaryAttendanceType: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'TARequestType' }],
      }),
    }),
    getDictionaryAttendanceReason: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'TAReason' }],
      }),
    }),
    getWorkLocationTypes: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'workLocation' }],
      }),
    }),
    getProjectNDepartments: builder.query<ProjectNDepartments[], string>({
      query: (empNo: string) => ({
        url: `${ApiBaseName.DICT}/emp/projdept`,
        method: 'get',
        params: { empNo },
      }),
    }),
    getProjectsOrDepartments: builder.mutation<
      ProjectNDepartments[],
      ProjectDepartmentsRequest
    >({
      query: (options) => ({
        url: `${ApiBaseName.DICT}/emp/projdept/additional-request`,
        method: 'get',
        params: options,
      }),
    }),
    getDeptProjList: builder.query<DeptProjList[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/projdept`,
        method: 'get',
      }),
    }),
    getDictionaryResignationStatuses: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'ResignStatus' }],
      }),
    }),
    getDictionaryPurchaseStatuses: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'PRStatus' }],
      }),
    }),
    getDictionaryResignationReasons: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'ResignReason' }],
      }),
    }),
    getOrganizations: builder.query<Organization[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/borgs`,
        method: 'get',
      }),
    }),
    getProjectsForOrg: builder.mutation<Project[], string>({
      query: (orgId: string) => ({
        url: `${ApiBaseName.DICT}/contracts-by-borgs`,
        method: 'get',
        params: { borgId: orgId },
      }),
    }),
    getRequisitionResources: builder.mutation<
      RequestListSearchResponse<RequisitionResources>,
      {
        ccId: number;
        resource: string;
      }
    >({
      query: ({ ccId, resource }) => ({
        url: `${ApiBaseName.DICT}/resources`,
        method: 'get',
        params: {
          ccId,
          resource,
        },
      }),
    }),
    getRequisitionResourceById: builder.mutation<
      RequisitionResources,
      {
        resourceId: string | number;
      }
    >({
      query: ({ resourceId }) => ({
        url: `${ApiBaseName.DICT}/resources/${resourceId}`,
        method: 'get',
      }),
    }),
    getPlantResources: builder.mutation<
      RequestListSearchResponse<PlantResources>,
      {
        compId: number;
        searchValue: string;
      }
    >({
      query: ({ compId, searchValue }) => ({
        url: `${ApiBaseName.DICT}/plants`,
        method: 'get',
        params: {
          compId,
          searchValue,
        },
      }),
    }),
    getPlantResourceById: builder.mutation<
      PlantResource,
      {
        plantId: string | number;
      }
    >({
      query: ({ plantId }) => ({
        url: `${ApiBaseName.DICT}/plants/${plantId}`,
        method: 'get',
      }),
    }),
    getActivitiesResources: builder.mutation<
      RequestListSearchResponse<ActivityResources>,
      {
        searchValue: string;
      }
    >({
      query: ({ searchValue }) => ({
        url: `${ApiBaseName.DICT}/activities`,
        method: 'get',
        params: {
          searchValue,
        },
      }),
      transformResponse(
        response: RequestListSearchResponse<ActivityResources>
      ) {
        const filtredActivities = response?.content?.filter(
          (activity) =>
            activity.actName !== 'Blank' || activity.actNumber.length > 2
        );
        response.content = filtredActivities || [];

        return response;
      },
    }),
    getActivitiesResourceById: builder.mutation<
      ActivityResources,
      {
        activityId: string | number;
      }
    >({
      query: ({ activityId }) => ({
        url: `${ApiBaseName.DICT}/activities/${activityId}`,
        method: 'get',
      }),
    }),
    getCostCenter: builder.mutation<
      CostCenter[],
      { compId: string | number; empNo: string; allocationId: string | number }
    >({
      query: (params) => ({
        url: `${ApiBaseName.DICT}/emp/cost-center`,
        method: 'get',
        params: params,
      }),
    }),
    getCostCenterType: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'Allocation' }],
      }),
      transformResponse(response: TypeDict[]) {
        // remove "Stock" item from response
        const filtredResponse = response.filter((item) => item.codeNum !== 2);

        return filtredResponse;
      },
    }),
    getWarantyType: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'WarrantyType' }],
      }),
    }),
    getMrfor: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'MRFor' }],
      }),
    }),
    getTenderFor: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'TenderFor' }],
      }),
    }),
    getTenderExtensionType: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'TenderExtensionType' }],
      }),
    }),
    getRFQStatus: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'RFQStatus' }],
      }),
    }),
    getTenderDocType: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'TenderDocType' }],
      }),
    }),
    getTenderMode: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'TenderMode' }],
      }),
    }),
    getVendors: builder.mutation<
      RequestListSearchResponse<VendorDTO>,
      {
        vendorName: string;
        page?: number;
        size?: number;
      }
    >({
      query: ({ vendorName, page = 0, size = 10 }) => ({
        url: `${ApiBaseName.DICT}/vendors`,
        method: 'get',
        params: { vendorName, page, size },
      }),
    }),
    getClassifications: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'VendorClass' }],
      }),
    }),
    getDocumentTypes: builder.query<TypeDict[], void>({
      query: () => ({
        url: `${ApiBaseName.DICT}/dict`,
        method: 'post',
        body: [{ dictCode: 'ProcDocType' }],
      }),
    }),
    getEmpPhoto: builder.query<string, string>({
      queryFn: async (fileName, { getState }) => {
        const token = extractAuthToken(getState);
        const response = await fetch(
          `${baseUrl}/${ApiBaseName.DICT}/profile-image/${fileName}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const imageBlob = await response.blob();
        const imageObjectURL = URL.createObjectURL(imageBlob);

        return {
          data: imageObjectURL,
        };
      },
    }),
    getEmpNumberByUserId: builder.query<string, number | undefined>({
      queryFn: async (userId: number | undefined, { getState }) => {
        const token = extractAuthToken(getState);

        const response = await fetch(
          `${baseUrl}/${ApiBaseName.DICT}/empuserid?userId=${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.text();

        return {
          data: result,
        };
      },
    }),
    getEmpNumberByUserIdFn: builder.mutation<string, number | undefined>({
      queryFn: async (userId: number | undefined, { getState }) => {
        const token = extractAuthToken(getState);

        const response = await fetch(
          `${baseUrl}/${ApiBaseName.DICT}/empuserid?userId=${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.text();

        return {
          data: result,
        };
      },
    }),
  }),
});

export const {
  useGetProfileAssetsQuery,
  useGetProfileLegalInfoQuery,
  useGetEmpNumberByUserIdFnMutation,
  useGetEmpNumberByUserIdQuery,
  useLazyGetEmpNumberByUserIdQuery,
  useGetDictionaryCurrencyQuery,
  useGetClassificationsQuery,
  useGetRequisitionResourcesMutation,
  useGetEmpPhotoQuery,
  useGetDictionaryRequestTypeQuery,
  useGetDictionaryLeaveTypeQuery,
  useGetDictionaryLeaveStatusesQuery,
  useGetDictionaryITReqItemsQuery,
  useGetDictionarySIMCapValueQuery,
  useGetDictionaryITAccessoriesQuery,
  useGetDictionaryPassportTypeQuery,
  useGetDictionaryPassportStatusesQuery,
  useGetDictionaryRequisitionStatusesQuery,
  useGetDictionarySalaryTypeQuery,
  useGetDictionarySalaryStatusesQuery,
  useGetDictionaryITReqStatusQuery,
  useGetDictionaryOverseasStatusesQuery,
  useGetDictionaryOverseasSponsorsQuery,
  useGetDictionaryCompaniesQuery,
  useGetDictionaryCountriesQuery,
  useGetDictionaryResumptionStatusesQuery,
  useGetDictionaryResumptionWorkShiftQuery,
  useGetDictionaryAttendanceStatusesQuery,
  useGetDictionaryAttendanceTypeQuery,
  useGetDictionaryAttendanceReasonQuery,
  useGetWorkLocationTypesQuery,
  useGetProjectNDepartmentsQuery,
  useGetProjectsOrDepartmentsMutation,
  useGetDeptProjListQuery,
  useGetDictionaryResignationStatusesQuery,
  useGetDictionaryResignationReasonsQuery,
  useGetOrganizationsQuery,
  useGetProjectsForOrgMutation,
  useGetCostCenterTypeQuery,
  useGetWarantyTypeQuery,
  useGetMrforQuery,
  useGetCostCenterMutation,
  useGetDocumentTypesQuery,
  useGetActivitiesResourceByIdMutation,
  useGetActivitiesResourcesMutation,
  useGetPlantResourcesMutation,
  useGetPlantResourceByIdMutation,
  useGetRequisitionResourceByIdMutation,
  useGetDictionaryPurchaseStatusesQuery,
  useGetTenderForQuery,
  useGetTenderExtensionTypeQuery,
  useGetRFQStatusQuery,
  useGetTenderDocTypeQuery,
  useGetTenderModeQuery,
  useGetVendorsMutation,
} = dictionaryApi;
